import { h, html, useStateSelector, useEventBus } from '../setup.js';
import { ClanUI } from '../clan/ClanUI.js';
import { CONFIG } from '../../../data/config.js';

export function GameView({ stateManager, eventBus, services }) {
  const { i18nService } = services;
  const resources = useStateSelector(stateManager, (state) => state.resources);
  const hero = useStateSelector(stateManager, (state) => state.hero);
  const systemState = useStateSelector(stateManager, (state) => state.system || {});

  // i18n Reaktivität
  const [lang, setLang] = useStateSelector(stateManager, () => i18nService.getLanguage());
  useEventBus(eventBus, 'i18n:languageChanged', (newLang) => {
    // Erzwinge re-rendering
    stateManager.dispatch((state) => ({ ...state }), 'i18n/refresh');
  });

  const timeWarpActive = systemState.timeWarpActive || false;
  const timeWarpCharge = systemState.timeWarpCharge || 0;
  const timeWarpRemaining = systemState.timeWarpRemaining || 0;
  
  const clickPowerLevel = hero?.clickPowerLevel || 0;
  let cost = Math.floor(CONFIG.GATHER.UPGRADE_BASE_COST * Math.pow(CONFIG.GATHER.UPGRADE_COST_MULT, clickPowerLevel));
  if (hero?.prestige?.activePact === 'ancient_folios') {
    cost = Math.floor(cost * 1.5);
  }
  const currentParticles = BigInt(resources?.particles || '0');
  const canUpgrade = currentParticles >= BigInt(cost);

  const handleManualGather = (e) => {
    eventBus.publish('game:manualGather', {
      clientX: e.clientX,
      clientY: e.clientY
    });
  };

  const handleUpgradeClick = () => {
    eventBus.publish('game:upgradeClickPower');
  };

  const handleOpenAchievements = () => {
    eventBus.publish('game:openAchievements');
  };

  const handleBackToHub = () => {
    eventBus.publish('game:backToHub');
  };

  const handleActivateTimeWarp = () => {
    if (timeWarpActive || timeWarpCharge < 100) return;
    
    stateManager.dispatch((state) => ({
      ...state,
      system: {
        ...state.system,
        timeWarpActive: true,
        timeWarpRemaining: 30,
        timeWarpCharge: 100
      }
    }), 'system/activateTimeWarp');
    
    eventBus.publish('timeWarp:started', {});
    const msg = lang === 'de'
      ? '🌀 Mneme-Zeitkrümmung entfesselt! 3-fache Spielgeschwindigkeit für 30 Sekunden!'
      : '🌀 Mneme Time Warp unleashed! 3x game speed for 30 seconds!';
    eventBus.publish('ui:showToast', {
      message: msg,
      type: 'success',
      duration: 4000
    });
  };

  return html`
    <section id="game-container" class="fade-in active ${timeWarpActive ? 'time-warp-blur-filter' : ''}" style="display: flex;" role="main" aria-label=${lang === 'de' ? 'Archiv des Vergessens – Spiel' : 'Archive of the Forgotten – Game'}>
      ${timeWarpActive ? html`<div class="time-warp-fullscreen-overlay"></div>` : ''}
      <!-- Game-Header -->
      <header id="game-header">
        <div class="header-left">
          <button id="back-to-hub-btn" class="glass-btn primary" onClick=${handleBackToHub} type="button" title=${lang === 'de' ? 'Zurück zur Übersicht' : 'Back to overview'}>${lang === 'de' ? '« Zurück zum Hub' : '« Back to Hub'}</button>
        </div>
        <div class="header-center">
          <h1 class="glow-text text-center">${lang === 'de' ? 'ARCHIV DES VERGESSENS' : 'ARCHIVE OF THE FORGOTTEN'}</h1>
          <p class="subtitle text-center">${lang === 'de' ? 'Der Mneme-Bund' : 'The Mneme Covenant'}</p>
        </div>
        <div class="header-right">
          <!-- Floating Time Warp Clock Button -->
          <button 
            class="glass-btn ${timeWarpActive ? 'btn-danger' : timeWarpCharge >= 100 ? 'primary epic-pulse' : ''}" 
            style="display: flex; flex-direction: column; align-items: center; justify-content: center; width: 64px; height: 64px; border-radius: 50%; padding: 0; margin-left: auto; box-shadow: ${timeWarpActive ? '0 0 20px rgba(255,50,50,0.4)' : timeWarpCharge >= 100 ? '0 0 20px rgba(197,160,89,0.4)' : 'none'}; transition: all 0.3s ease;"
            disabled=${!timeWarpActive && timeWarpCharge < 100}
            onClick=${handleActivateTimeWarp}
            type="button"
            title=${timeWarpActive ? (lang === 'de' ? `Zeitkrümmung aktiv: noch ${Math.ceil(timeWarpRemaining)}s` : `Time warp active: ${Math.ceil(timeWarpRemaining)}s remaining`) : timeWarpCharge >= 100 ? (lang === 'de' ? 'Klicke, um 3x Zeitkrümmung zu aktivieren!' : 'Click to activate 3x Time Warp!') : (lang === 'de' ? `Fokus aufladen: ${Math.floor(timeWarpCharge)}%` : `Charging focus: ${Math.floor(timeWarpCharge)}%`)}
          >
            <span style="font-size: 1.6rem; line-height: 1; filter: drop-shadow(0 0 5px currentColor);">${timeWarpActive ? '⏳' : '🌀'}</span>
            <span class="cinzel glow-text" style="font-size: 0.65rem; font-weight: bold; margin-top: 4px; color: ${timeWarpActive || timeWarpCharge >= 100 ? '#fff' : 'var(--color-gold)'};">
              ${timeWarpActive ? `${Math.ceil(timeWarpRemaining)}s` : `${Math.floor(timeWarpCharge)}%`}
            </span>
          </button>
        </div>
      </header>

      <!-- Ressourcen-Leiste -->
      <div id="resource-bar" class="resource-bar-flex glass-panel" role="status" aria-label=${lang === 'de' ? 'Ressourcen' : 'Resources'}>
        <div class="resource-group">
          <div class="resource-item" id="res-particle">
            <span class="resource-label">${lang === 'de' ? 'Partikel:' : 'Particles:'}</span>
            <span class="resource-value text-gold glow-text" id="particle-display">${resources?.particles || '0'}</span>
          </div>
          <div class="resource-item" id="res-relic">
            <span class="resource-label">${lang === 'de' ? 'Relikte:' : 'Relics:'}</span>
            <span class="resource-value text-gold glow-text" id="relic-display">${resources?.relics || '0'}</span>
          </div>
          <div class="resource-item" id="res-artifact">
            <span class="resource-label">${lang === 'de' ? 'Artefakte:' : 'Artifacts:'}</span>
            <span class="resource-value text-gold glow-text" id="node-artifact-display">${resources?.artifacts || '0'}</span>
          </div>
        </div>
        <button id="open-achievements-btn" class="glass-btn primary" onClick=${handleOpenAchievements} type="button">${lang === 'de' ? '🏆 Erfolge ansehen' : '🏆 View Achievements'}</button>
      </div>

      <!-- Sammel-Bereich -->
      <div class="gather-section">
        <button id="manual-gather-btn" class="primary epic-pulse text-gold cinzel" onClick=${handleManualGather} type="button">${lang === 'de' ? '✨ Mneme-Partikel extrahieren ✨' : '✨ Extract Mneme Particles ✨'}</button>
        <button id="upgrade-click-btn" class="glass-btn ${canUpgrade ? 'text-highlight' : 'text-muted'}" disabled=${!canUpgrade} onClick=${handleUpgradeClick} type="button">
          ${lang === 'de' ? `Klick-Stärke verbessern (Kosten: ${cost} Partikel)` : `Improve Click Power (Cost: ${cost} Particles)`}
        </button>
      </div>

      <!-- Panels: Clan & Rekrutierung (Direkt eingebunden) -->
      <${ClanUI} stateManager=${stateManager} eventBus=${eventBus} services=${services} />
    </section>
  `;
}

export default GameView;
